'use client';

import React from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


// ─── Base Card ────────────────────────────────────────────────────────────────

interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'sm' | 'md' | 'lg';
  hover?: boolean;
  alert?: boolean;
  alertVariant?: 'warning' | 'danger';
  style?: React.CSSProperties;
}

export function Card({ children, className = '', padding = 'md', hover = false, alert = false, alertVariant = 'warning', style }: CardProps) {
  const padMap = { sm: 'p-3', md: 'p-4', lg: 'p-5' };
  const alertClass = alert
    ? alertVariant === 'danger' ?'border-red-200 bg-red-50' :'border-amber-200 bg-amber-50' :'border-border/60 bg-card';
  return (
    <div
      className={`rounded-xl border shadow-sm ${hover ? 'hover:shadow-md transition-all duration-200' : ''} ${padMap[padding]} ${alertClass} ${className}`}
      style={style}
    >
      {children}
    </div>
  );
}

// ─── Icon Container ───────────────────────────────────────────────────────────

interface IconContainerProps {
  icon: React.ElementType;
  bg?: string;
  color?: string;
  size?: 'sm' | 'md';
  className?: string;
}

export function IconContainer({ icon: Icon, bg = '#f1f5f9', color = '#475569', size = 'md', className = '' }: IconContainerProps) {
  const sizeClass = size === 'sm' ? 'h-8 w-8' : 'h-9 w-9';
  const iconSize = size === 'sm' ? 14 : 16;
  return (
    <div
      className={`flex ${sizeClass} items-center justify-center rounded-lg flex-shrink-0 ${className}`}
      style={{ backgroundColor: bg }}
    >
      <Icon size={iconSize} style={{ color }} />
    </div>
  );
}

// ─── Trend Indicator ──────────────────────────────────────────────────────────

interface TrendProps {
  change: number;
  label?: string;
  invertColors?: boolean;
}

export function TrendIndicator({ change, label, invertColors = false }: TrendProps) {
  const isPositive = change > 0;
  const isNegative = change < 0;
  const goodColor = invertColors ? 'text-red-600' : 'text-green-600';
  const badColor = invertColors ? 'text-green-600' : 'text-red-600';
  const color = isPositive ? goodColor : isNegative ? badColor : 'text-muted-foreground';

  return (
    <div className={`flex items-center gap-0.5 text-xs font-medium ${color}`}>
      {isPositive ? <ArrowUpRight size={12} /> : isNegative ? <ArrowDownRight size={12} /> : <Minus size={12} />}
      <span className="font-tabular">
        {change !== 0 ? `${Math.abs(change)}%` : '—'}
        {label ? ` ${label}` : ''}
      </span>
    </div>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  iconBg?: string;
  iconColor?: string;
  change?: number;
  changeLabel?: string;
  invertTrend?: boolean;
  alert?: boolean;
  alertVariant?: 'warning' | 'danger';
  href?: string;
  className?: string;
}

export function StatCard({
  label, value, sub, icon, iconBg = '#f1f5f9', iconColor = '#475569',
  change, changeLabel, invertTrend = false, alert = false, alertVariant = 'warning',
  href, className = ''
}: StatCardProps) {
  const alertClass = alert
    ? alertVariant === 'danger' ?'border-red-200 bg-red-50' :'border-amber-200 bg-amber-50' :'border-border/60 bg-card';
  const valueColor = alert
    ? alertVariant === 'danger' ? 'text-red-700' : 'text-amber-700' :'text-foreground';

  const inner = (
    <div className={`rounded-xl border shadow-sm p-4 flex items-start gap-3 h-full hover:shadow-md transition-all duration-200 ${alertClass} ${className}`}>
      <IconContainer icon={icon} bg={iconBg} color={iconColor} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-muted-foreground leading-tight truncate">{label}</p>
        <p className={`text-xl sm:text-2xl font-semibold tracking-tight mt-0.5 font-tabular leading-tight ${valueColor}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{sub}</p>}
        {change !== undefined && (
          <div className="mt-1">
            <TrendIndicator change={change} label={changeLabel} invertColors={invertTrend} />
          </div>
        )}
      </div>
    </div>
  );

  return href ? <Link href={href} className="block h-full">{inner}</Link> : inner;
}

// ─── KPICard (icon top-right layout) ─────────────────────────────────────────

interface KPICardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  iconBg?: string;
  iconColor?: string;
  change?: number;
  changeLabel?: string;
  invertTrend?: boolean;
  variant?: 'default' | 'hero' | 'success' | 'warning' | 'danger' | 'info';
  href?: string;
  className?: string;
}

const VARIANT_MAP: Record<string, { bg: string; iconBg: string; iconColor: string; border?: string; textColor: string; mutedColor: string }> = {
  default:  { bg: 'var(--card)', iconBg: '#f1f5f9', iconColor: '#475569', textColor: 'var(--foreground)', mutedColor: 'var(--muted-foreground)' },
  hero:     { bg: 'var(--primary)', iconBg: 'rgba(255,255,255,0.15)', iconColor: '#ffffff', border: 'none', textColor: '#ffffff', mutedColor: 'rgba(255,255,255,0.75)' },
  success:  { bg: 'var(--card)', iconBg: '#f0fdf4', iconColor: '#15803d', textColor: 'var(--foreground)', mutedColor: 'var(--muted-foreground)' },
  warning:  { bg: '#fffdf5', iconBg: '#fffbeb', iconColor: '#d97706', border: '#fde68a', textColor: 'var(--foreground)', mutedColor: '#92400e' },
  danger:   { bg: '#fff8f8', iconBg: '#fef2f2', iconColor: '#dc2626', border: '#fecaca', textColor: 'var(--foreground)', mutedColor: '#991b1b' },
  info:     { bg: 'var(--card)', iconBg: '#eff6ff', iconColor: '#2563eb', textColor: 'var(--foreground)', mutedColor: 'var(--muted-foreground)' },
};

export function KPICard({
  label, value, sub, icon: Icon, iconBg, iconColor,
  change, changeLabel, invertTrend = false,
  variant = 'default', href, className = ''
}: KPICardProps) {
  const style = VARIANT_MAP[variant];
  const iBg = iconBg ?? style.iconBg;
  const iColor = iconColor ?? style.iconColor;
  const isHero = variant === 'hero';

  const trendColor = isHero
    ? 'rgba(255,255,255,0.85)'
    : change !== undefined && change > 0
      ? (invertTrend ? '#dc2626' : '#16a34a')
      : change !== undefined && change < 0
        ? (invertTrend ? '#16a34a' : '#dc2626')
        : 'var(--muted-foreground)';

  const inner = (
    <div
      className={`rounded-xl border shadow-sm p-4 h-full hover:shadow-md transition-all duration-200 ${className}`}
      style={{ backgroundColor: style.bg, borderColor: style.border ?? 'var(--border)' }}
    >
      <div className="flex items-start justify-between mb-2">
        <p className="text-xs font-medium leading-tight" style={{ color: style.mutedColor }}>{label}</p>
        <div className="flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: iBg }}>
          <Icon size={14} style={{ color: iColor }} />
        </div>
      </div>
      <p className="text-xl sm:text-2xl font-semibold tracking-tight font-tabular leading-tight" style={{ color: style.textColor }}>{value}</p>
      {sub && <p className="text-xs mt-0.5 leading-tight" style={{ color: style.mutedColor }}>{sub}</p>}
      {change !== undefined && (
        <div className="flex items-center gap-0.5 mt-1.5 text-xs font-medium" style={{ color: trendColor }}>
          {change > 0 ? <ArrowUpRight size={12} /> : change < 0 ? <ArrowDownRight size={12} /> : <Minus size={12} />}
          <span className="font-tabular">{change !== 0 ? `${Math.abs(change)}%` : '—'}{changeLabel ? ` ${changeLabel}` : ''}</span>
        </div>
      )}
    </div>
  );

  return href ? <Link href={href} className="block h-full">{inner}</Link> : inner;
}

// ─── MetricCard (compact, icon-left) ─────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ElementType;
  iconBg?: string;
  iconColor?: string;
  className?: string;
}

export function MetricCard({ label, value, sub, icon: Icon, iconBg = '#f1f5f9', iconColor = '#475569', className = '' }: MetricCardProps) {
  return (
    <div className={`rounded-xl border border-border/60 bg-card shadow-sm p-3 flex flex-col gap-1.5 hover:shadow-md transition-all duration-200 ${className}`}>
      <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: iconBg }}>
        <Icon size={14} style={{ color: iconColor }} />
      </div>
      <p className={`text-lg font-semibold tracking-tight font-tabular leading-tight`} style={{ color: iconColor }}>{value}</p>
      <p className="text-xs font-medium text-foreground leading-tight">{label}</p>
      {sub && <p className="text-xs text-muted-foreground leading-tight">{sub}</p>}
    </div>
  );
}

// ─── ChartCard ────────────────────────────────────────────────────────────────

interface ChartCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  padding?: 'sm' | 'md';
}

export function ChartCard({ title, subtitle, children, action, className = '', padding = 'md' }: ChartCardProps) {
  const padClass = padding === 'sm' ? 'p-3' : 'p-4';
  return (
    <div className={`rounded-xl border border-border/60 bg-card shadow-sm ${padClass} ${className}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground leading-tight">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
      {children}
    </div>
  );
}

// ─── InfoCard ─────────────────────────────────────────────────────────────────

interface InfoCardProps {
  title: string;
  children: React.ReactNode;
  icon?: React.ElementType;
  action?: React.ReactNode;
  className?: string;
}

export function InfoCard({ title, children, icon: Icon, action, className = '' }: InfoCardProps) {
  return (
    <div className={`rounded-xl border border-border/60 bg-card shadow-sm p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && (
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Icon size={14} className="text-primary" />
            </div>
          )}
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
        {action && <div>{action}</div>}
      </div>
      {children}
    </div>
  );
}

// ─── AlertCard ────────────────────────────────────────────────────────────────

interface AlertCardProps {
  title: string;
  message: string;
  variant?: 'warning' | 'danger' | 'info' | 'success';
  icon?: React.ElementType;
  action?: React.ReactNode;
  className?: string;
}

const ALERT_STYLES = {
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', title: 'text-amber-800', msg: 'text-amber-700', iconColor: '#d97706' },
  danger:  { bg: 'bg-red-50',   border: 'border-red-200',   title: 'text-red-800',   msg: 'text-red-700',   iconColor: '#dc2626' },
  info:    { bg: 'bg-blue-50',  border: 'border-blue-200',  title: 'text-blue-800',  msg: 'text-blue-700',  iconColor: '#2563eb' },
  success: { bg: 'bg-green-50', border: 'border-green-200', title: 'text-green-800', msg: 'text-green-700', iconColor: '#16a34a' },
};

export function AlertCard({ title, message, variant = 'warning', icon: Icon, action, className = '' }: AlertCardProps) {
  const s = ALERT_STYLES[variant];
  return (
    <div className={`rounded-xl border ${s.bg} ${s.border} p-4 ${className}`}>
      <div className="flex items-start gap-3">
        {Icon && <Icon size={16} style={{ color: s.iconColor }} className="flex-shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${s.title}`}>{title}</p>
          <p className={`text-xs mt-0.5 ${s.msg}`}>{message}</p>
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
    </div>
  );
}

// ─── SummaryCard (label + value stacked, no icon) ────────────────────────────

interface SummaryCardProps {
  label: string;
  value: string | number;
  sub?: string;
  valueColor?: string;
  className?: string;
}

export function SummaryCard({ label, value, sub, valueColor = 'text-foreground', className = '' }: SummaryCardProps) {
  return (
    <div className={`rounded-xl border border-border/60 bg-card shadow-sm p-4 ${className}`}>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight">{label}</p>
      <p className={`text-xl sm:text-2xl font-semibold tracking-tight font-tabular mt-1 leading-tight ${valueColor}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{sub}</p>}
    </div>
  );
}

// ─── ActionCard ───────────────────────────────────────────────────────────────

interface ActionCardProps {
  title: string;
  description?: string;
  icon?: React.ElementType;
  iconBg?: string;
  iconColor?: string;
  action: React.ReactNode;
  className?: string;
}

export function ActionCard({ title, description, icon: Icon, iconBg = '#f1f5f9', iconColor = '#475569', action, className = '' }: ActionCardProps) {
  return (
    <div className={`rounded-xl border border-border/60 bg-card shadow-sm p-4 flex items-center gap-3 hover:shadow-md transition-all duration-200 ${className}`}>
      {Icon && <IconContainer icon={Icon} bg={iconBg} color={iconColor} />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground leading-tight">{title}</p>
        {description && <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{description}</p>}
      </div>
      <div className="flex-shrink-0">{action}</div>
    </div>
  );
}
